import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { CirclePlus, Loader } from "lucide-react";
import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import { colors } from "@/utils/colors";
import { toast } from "sonner";
import { createMarketplaceSale } from "@/Api/marketplace";
import { getContestProductsUnityBalances } from "@/Api/leftovers";
import { getSelectContests } from "@/Api/contests";
import { getSelectProductGroups } from "@/Api/productGroups";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ContestProductBalance } from "@/types/leftovers";
import { Mask } from "@/utils/mask";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  ColoredProductCard,
  ColoredProductIcon,
} from "@/components/ui/colored-product-card";

interface ConfirmationData {
  products: Array<{
    id: number;
    quantity: number;
    product: ContestProductBalance;
  }>;
  payload: {
    unity_id: number;
    sale_type_id: number;
    contest_product_classifications: Array<{
      id: number;
      quantity: number;
    }>;
  };
}

interface AddBtnProps {
  updateFront: () => void;
}

export const AddBtn = ({ updateFront }: AddBtnProps) => {
  const [isLoad, setIsLoad] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [lotteries, setLotteries] = useState<Array<{ id: number; name: string; color: string }>>([]);
  const [contests, setContests] = useState<Array<{ id: number; name: string }>>([]);
  const [contestProducts, setContestProducts] = useState<ContestProductBalance[]>([]);
  const [selectedLottery, setSelectedLottery] = useState("");
  const [selectedContest, setSelectedContest] = useState("");
  const [selectedProducts, setSelectedProducts] = useState<Array<{ id: number; quantity: number }>>([]);
  const [confirmationData, setConfirmationData] = useState<ConfirmationData | null>(null);

  useEffect(() => {
    if (isOpen) {
      fetchInitialData();
    }
  }, [isOpen]);

  useEffect(() => {
    if (selectedLottery) {
      fetchContests();
    }
  }, [selectedLottery]);

  useEffect(() => {
    if (selectedContest) {
      fetchContestProducts();
    }
  }, [selectedContest]);

  const fetchInitialData = async () => {
    try {
      const lotteriesResponse = await getSelectProductGroups();
      setLotteries(lotteriesResponse.data?.response || []);
    } catch (error) {
      console.error("Erro ao buscar dados iniciais:", error);
      toast.error("Erro ao carregar dados");
    }
  };

  const fetchContests = async () => {
    try {
      const contestsResponse = await getSelectContests(parseInt(selectedLottery));
      setContests(contestsResponse.data?.response || []);
      setSelectedContest(""); // Reset contest when lottery changes
      setContestProducts([]); // Reset products
      setSelectedProducts([]); // Reset selected products
    } catch (error) {
      console.error("Erro ao buscar concursos:", error);
      toast.error("Erro ao carregar concursos");
    }
  };

  const fetchContestProducts = async () => {
    try {
      // Para marketplace, usamos unity_id fixo = 5
      const { data } = await getContestProductsUnityBalances(
        "5", // Unity ID fixo para marketplace
        selectedContest
      );
      const productsWithBalance = (data?.response || []).filter(
        (product: ContestProductBalance) => product.unity_balance > 0
      );
      setContestProducts(productsWithBalance);
    } catch (error) {
      console.error("Erro ao buscar produtos do concurso:", error);
      toast.error("Erro ao carregar produtos do concurso");
    }
  };

  const formSubmit = async () => {
    const contest_product_classifications = selectedProducts
      .filter(selectedProduct => selectedProduct.quantity > 0)
      .map((selectedProduct) => {
        const product = contestProducts.find(p => p.cp_classification_id === selectedProduct.id);
        if (!product) return null;
        
        if (selectedProduct.quantity > product.unity_balance) {
          throw new Error(
            `Quantidade para ${product.contest_product_classification?.product_name} não pode exceder o saldo disponível (${product.unity_balance})`
          );
        }
        return {
          id: selectedProduct.id,
          quantity: selectedProduct.quantity,
          product: product,
        };
      })
      .filter((item): item is NonNullable<typeof item> => item !== null);

    if (contest_product_classifications.length === 0) {
      toast.error("Selecione pelo menos um produto com quantidade maior que 0");
      return;
    }

    setConfirmationData({
      products: contest_product_classifications,
      payload: {
        unity_id: 5, // Unity ID fixo para marketplace
        sale_type_id: 5, // Sale Type ID fixo para marketplace
        contest_product_classifications: contest_product_classifications.map(p => ({
          id: p.id,
          quantity: p.quantity,
        })),
      },
    });

    setIsConfirmOpen(true);
  };

  const handleProductSelection = (productId: number, quantity: number) => {
    setSelectedProducts((prev) => {
      const existing = prev.find((p) => p.id === productId);
      if (existing) {
        return prev.map((p) => (p.id === productId ? { ...p, quantity } : p));
      }
      return [...prev, { id: productId, quantity }];
    });
  };

  const handleConfirmCreate = async () => {
    if (!confirmationData) return;
    
    setIsLoad(true);
    
    try {
      const { ok } = await createMarketplaceSale(confirmationData.payload);
      
      if (ok) {
        toast.success("Venda marketplace criada com sucesso!");
      } else {
        toast.error("Erro ao criar venda marketplace");
      }
      
      setIsOpen(false);
      setIsConfirmOpen(false);

      updateFront();
    } catch (error) {
      console.error("Erro ao criar venda marketplace:", error);
      toast.error("Erro ao criar venda marketplace");
    } finally {
      setIsLoad(false);
    }
  };

  useEffect(() => {
    if (!isOpen) {
      setSelectedLottery("");
      setSelectedContest("");
      setSelectedProducts([]);
      setContests([]);
      setContestProducts([]);
      setConfirmationData(null);
      setIsConfirmOpen(false);
    }
  }, [isOpen]);

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger>
        <Button
          variant="outline"
          className={cn("py-3 flex gap-1", `bg-primary`)}
        >
          <CirclePlus className="size-5" strokeWidth={1.75} />
          Criar Venda Marketplace
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[900px] max-h-[90vh] overflow-y-auto">
        <DialogHeader className="flex flex-col gap-2">
          <DialogDescription className="text-lg">
            Criar Venda Marketplace
          </DialogDescription>
          <DialogTitle className="text-2xl">Selecione o Concurso e Produtos</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Seleção de Loteria */}
          <div>
            <label className="text-lg font-semibold block mb-2">
              Selecione a Loteria:
            </label>
            <Select
              onValueChange={(value) => {
                setSelectedLottery(value);
                setSelectedProducts([]); // Reset produtos quando mudar loteria
              }}
              value={selectedLottery}
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecione a loteria" />
              </SelectTrigger>
              <SelectContent>
                {lotteries.map((lottery) => (
                  <SelectItem key={lottery.id} value={String(lottery.id)}>
                    <div className="flex items-center gap-2">
                      <div
                        className="w-3 h-3 rounded-full border"
                        style={{ backgroundColor: lottery.color || '#6b7280' }}
                      />
                      {lottery.name}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Seleção de Concurso */}
          {selectedLottery && (
            <div>
              <label className="text-lg font-semibold block mb-2">
                Selecione o Concurso:
              </label>
              <Select
                onValueChange={(value) => {
                  setSelectedContest(value);
                  setSelectedProducts([]); // Reset produtos quando mudar concurso
                }}
                value={selectedContest}
                disabled={contests.length === 0}
              >
                <SelectTrigger>
                  <SelectValue placeholder={contests.length === 0 ? "Sem concursos" : "Selecione o concurso"} />
                </SelectTrigger>
                <SelectContent>
                  {contests.length === 0 ? (
                    <div className="py-2 px-3 text-sm text-gray-500 text-center">
                      Sem concursos
                    </div>
                  ) : (
                    contests.map((contest) => (
                      <SelectItem key={contest.id} value={String(contest.id)}>
                        {contest.name}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Produtos do Concurso */}
          {selectedContest && contestProducts.length > 0 && (
            <div className="space-y-4">
              <h3 className="text-xl font-semibold text-center">Selecione os Produtos</h3>
              
              {/* Agrupar produtos por loteria como no sales */}
              {(() => {
                const groupedProducts = contestProducts.reduce((acc, product) => {
                  const lotteryId = product.contest?.lottery_id;
                  if (lotteryId && !acc[lotteryId]) {
                    acc[lotteryId] = [];
                  }
                  if (lotteryId) {
                    acc[lotteryId].push(product);
                  }
                  return acc;
                }, {} as Record<number, ContestProductBalance[]>);

                return Object.entries(groupedProducts).map(([lotteryId, lotteryProducts]) => {
                  const lotteryColor = "#007BFF"; // Default color since API doesn't provide color
                  const lotteryName = lotteryProducts[0]?.contest?.lottery?.name;
                  
                  const lightenColor = (color: string, percent: number) => {
                    const num = parseInt(color.replace("#", ""), 16);
                    const amt = Math.round(2.55 * percent);
                    const R = (num >> 16) + amt;
                    const G = ((num >> 8) & 0x00ff) + amt;
                    const B = (num & 0x0000ff) + amt;
                    return (
                      "#" +
                      (
                        0x1000000 +
                        (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 +
                        (G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 +
                        (B < 255 ? (B < 1 ? 0 : B) : 255)
                      )
                        .toString(16)
                        .slice(1)
                    );
                  };

                  const lightLotteryColor = lotteryColor ? lightenColor(lotteryColor, 80) : "#f3f4f6";

                  return (
                    <Card key={lotteryId} className="mb-6">
                      <CardContent className="p-4">
                        <div
                          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 p-4 rounded-lg"
                          style={{
                            backgroundColor: lotteryColor,
                          }}
                        >
                          <div className="col-span-full mb-4">
                            <h2 className="text-2xl font-bold text-center text-white">
                              {lotteryName}
                            </h2>
                          </div>

                          {lotteryProducts.map((product) => {
                            const selectedProduct = selectedProducts.find(
                              (p) => p.id === product.cp_classification_id
                            );
                            const isOverLimit = (selectedProduct?.quantity || 0) > product.unity_balance;

                            return (
                              <ColoredProductCard
                                key={product.cp_classification_id}
                                bgColor={isOverLimit ? "#ef4444" : lightLotteryColor}
                              >
                                <div className="flex flex-col gap-2 w-full">
                                  <div className="flex items-center gap-2">
                                    <ColoredProductIcon bgColor={lotteryColor} />
                                    <p className="text-sm font-medium text-white">
                                      {lotteryName}
                                    </p>
                                  </div>
                                  <span className="text-sm flex flex-row gap-2">
                                    <h4 className="font-semibold text-white">
                                      {product.contest_product_classification?.product_name}
                                    </h4>
                                    <span className="text-white">-</span>
                                    <span className="text-white">
                                      {product.contest_product_classification?.product_classification?.name}
                                    </span>
                                  </span>
                                  <div className="flex items-center gap-4">
                                    <div className="text-sm font-medium text-white">
                                      Disponível: {product.unity_balance}
                                    </div>
                                    <div className="text-sm font-bold text-yellow-300">
                                      Vendidas: {product.sold_quantity ?? 0}
                                    </div>
                                  </div>
                                  <div className="text-xs text-white/80">
                                    Valor: {Mask.formatCurrency(product.contest_product_classification?.product_classification?.sale_price || 0)}
                                  </div>
                                  <div className="mt-2">
                                    <Input
                                      type="number"
                                      min="0"
                                      max={product.unity_balance}
                                      placeholder="Quantidade"
                                      value={selectedProduct?.quantity || ""}
                                      onChange={(e) => {
                                        const quantity = parseInt(e.target.value) || 0;
                                        if (quantity > product.unity_balance) {
                                          toast.error(`Quantidade máxima disponível: ${product.unity_balance}`);
                                          return;
                                        }
                                        handleProductSelection(product.cp_classification_id, quantity);
                                      }}
                                      className={`w-full bg-white ${
                                        isOverLimit ? "border-red-500" : ""
                                      }`}
                                      disabled={product.unity_balance === 0}
                                    />
                                    {isOverLimit && (
                                      <p className="text-red-200 text-xs mt-1 font-medium">
                                        Quantidade excede o limite disponível
                                      </p>
                                    )}
                                  </div>
                                </div>
                              </ColoredProductCard>
                            );
                          })}
                        </div>
                      </CardContent>
                    </Card>
                  );
                });
              })()}
            </div>
          )}

          {selectedContest && contestProducts.length === 0 && (
            <div className="text-center p-6 text-gray-500">
              <p>Nenhum produto disponível para este concurso.</p>
            </div>
          )}
        </div>

        <DialogFooter className="mt-6">
          <Button
            onClick={formSubmit}
            className={cn("flex gap-2 mt-1", colors.bg_primary)}
            disabled={isLoad || contestProducts.length === 0 || !selectedLottery || !selectedContest || selectedProducts.filter(p => p.quantity > 0).length === 0}
          >
            {isLoad ? (
              <>
                <Loader className="animate-spin size-4" /> Criando
              </>
            ) : (
              <>Criar Venda Marketplace</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>

      {/* Modal de Confirmação */}
      <Dialog open={isConfirmOpen} onOpenChange={setIsConfirmOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Confirmar Criação de Venda Marketplace</DialogTitle>
            <DialogDescription>
              Revise os dados antes de criar a venda
            </DialogDescription>
          </DialogHeader>
          
          {confirmationData && (
            <div className="space-y-4">
              <div className="text-center p-4 bg-blue-50 rounded-lg">
                <h4 className="font-medium text-blue-800">Venda Marketplace</h4>
                <p className="text-sm text-blue-600">Unidade: Marketplace | Tipo: Marketplace</p>
              </div>
              
              <div>
                <h4 className="font-medium text-sm text-gray-700 mb-2">Produtos selecionados</h4>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {confirmationData.products.map((item) => (
                    <div key={item.id} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                      <div className="flex-1">
                        <div className="font-medium text-sm">
                          {item.product.contest_product_classification?.product_name}
                        </div>
                        <div className="text-xs text-gray-500">
                          {item.product.contest_product_classification?.product_classification?.name}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-bold text-blue-600">
                          {item.quantity} cotas
                        </div>
                        <div className="text-xs text-gray-500">
                          {Mask.formatCurrency((item.product.contest_product_classification?.product_classification?.sale_price || 0) * item.quantity)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="border-t pt-4">
                <div className="flex justify-between items-center font-medium">
                  <span>Total de cotas:</span>
                  <span className="text-blue-600">
                    {confirmationData.products.reduce((total, item) => total + item.quantity, 0)} cotas
                  </span>
                </div>
                <div className="flex justify-between items-center font-medium">
                  <span>Valor total:</span>
                  <span className="text-green-600">
                    {Mask.formatCurrency(confirmationData.products.reduce((total, item) => {
                      const value = item.product.contest_product_classification?.product_classification?.sale_price || 0;
                      return total + (value * item.quantity);
                    }, 0))}
                  </span>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsConfirmOpen(false)}
              disabled={isLoad}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleConfirmCreate}
              className={cn("flex gap-2", colors.bg_primary)}
              disabled={isLoad}
            >
              {isLoad ? (
                <>
                  <Loader className="animate-spin size-4" /> Criando
                </>
              ) : (
                <>Confirmar e Criar</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};